/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from "axios";

import { ErrorAction } from "../reducers/slices/errorSlice";
import GroupPromise from "./GroupPromise";
import { logout } from "../pages/unauthenticated/ducks/operations";
import { UnauthenticatedActions } from "../pages/unauthenticated/ducks/slices";
import {
  ERROR_CODE,
  handleNetworkFailure,
  notifyRequestSucceeded,
  trimObject,
} from "@advapacs/common";

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry: boolean;
}

interface CustomAxiosError extends AxiosError {
  config: CustomAxiosRequestConfig;
}

const TIMEOUT = 1 * 60 * 1000;
axios.defaults.timeout = TIMEOUT;
axios.defaults.withCredentials = true;
axios.defaults.baseURL = import.meta.env.VITE_APP_API_URL;

/**
 * Error Code
 * 1003: 	User is disabled
 * 1004: Invalid credentials
 * 1005: Invalid 2FA
 * 1013: SAML Authentication Failure
 * 1014: MFA Enrollment Required
 */
const INVALID_CREDENTIALS_CODE = ["1003", "1004", "1005", "1013", "1014"];

const setupAxiosInterceptors = (store: any): void => {
  const onRequestSuccess = (config: InternalAxiosRequestConfig) => {
    const token = store.getState().unAuthenticatedReducer?.csrfToken;
    const lang =
      store.getState().unAuthenticatedReducer?.account?.localisation?.locale ||
      window.navigator.language;
    if (token) {
      axios.defaults.headers.common["X-XSRF-TOKEN"] = token;
      axios.defaults.headers.common["Accept-Language"] = lang;
      axios.defaults.headers.common["X-TZ"] =
        Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    if (config.data) {
      config.data = trimObject(config.data);
    }
    if (config.params) {
      // Trim each parameter value
      Object.keys(config.params).forEach((key) => {
        if (config.params[key] && typeof config.params[key] === "string") {
          config.params[key] = config.params[key].trim();
        }
      });
    }
    return config;
  };
  const onResponseSuccess = (response: AxiosResponse) => {
    if (store) store.dispatch(ErrorAction.setHideIndicator());
    notifyRequestSucceeded();
    return response;
  };
  const onResponseError = async (err: CustomAxiosError) => {
    const status = err?.response?.status;
    if (status === undefined) {
      // Shown by NetworkStatusAlert ("Unable to connect to API" / offline).
      return handleNetworkFailure(err, {
        onFailure: () => {
          // Full-page loader only while bootstrapping (e.g. /csrf fails); after that
          // keep the page interactive so the user can retry.
          if (store.getState().unAuthenticatedReducer?.sessionHasBeenFetched)
            store.dispatch(ErrorAction.setHideIndicator());
          else store.dispatch(ErrorAction.setShowIndicator());
        },
      });
    }
    // Handle common for recaptcha trace exception
    if (
      status === 400 &&
      ["RecaptchaRiskException", "RecaptchaInvalidException"].includes(
        // @ts-ignore
        err?.response?.data?.type
      )
    ) {
      return Promise.reject({
        ...err,
        response: {
          ...err.response,
          data: {
            // @ts-ignore
            ...err.response.data,
            message:
              "This action has been blocked due to suspicious activity. If you think this was a mistake, please contact support and provide ID: " +
              err?.response?.headers["x-trace-id"],
          },
        },
      });
    }

    store.dispatch(ErrorAction.setError(err?.response?.data));

    const apiError = err?.response?.data;
    // @ts-ignore
    const errorCode = apiError?.code;
    const sessionHasBeenFetched =
      store.getState().unAuthenticatedReducer?.sessionHasBeenFetched;
    const isAuthenticated =
      store.getState().unAuthenticatedReducer?.isAuthenticated;
    if (
      // @ts-ignore
      errorCode === ERROR_CODE.UNAUTHENTICATED &&
      isAuthenticated &&
      sessionHasBeenFetched &&
      !["/"].includes(window.location.pathname)
    ) {
      store.dispatch(UnauthenticatedActions.clearAuthentication());
      localStorage.setItem("path", window.location.pathname);
      window.location.href = "/";
    }
    // 1009: IP Address Forbidden
    // @ts-ignore
    if (
      errorCode === ERROR_CODE.IP_FORBIDDEN &&
      isAuthenticated &&
      sessionHasBeenFetched
    ) {
      store.dispatch(logout());
      return;
    }

    if (
      !window.location.pathname.includes("/t/") &&
      status === 401 &&
      // @ts-ignore
      ((!INVALID_CREDENTIALS_CODE.includes(errorCode) &&
        sessionHasBeenFetched) ||
        // @ts-ignore
        errorCode === "1007") // Expired authentication
    ) {
      const originalRequest = err.config;
      if (!originalRequest?._retry) {
        originalRequest._retry = true;
        const promise = GroupPromise.execute("/auth/refresh");
        if (promise) {
          try {
            await promise;
          } catch (e) {
            store.dispatch(UnauthenticatedActions.clearAuthentication());
          }
          return axios.request(originalRequest);
        }
      }
    }

    return Promise.reject(err);
  };
  // @ts-ignore
  axios.interceptors.request.use(onRequestSuccess);
  axios.interceptors.response.use(onResponseSuccess, onResponseError);
};

export default setupAxiosInterceptors;
